import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, CircularProgress, Stack } from "@mui/material";

import { Paginator, PaginatorTemplate } from "primereact/paginator";

import config from "../../../config";
import LineChart from "../../shared/charts/LineChart";
import { WaveformConstantValues } from "../../shared/constants/UTP2WaveformConstants";
import { useInternationalFormatters } from "../../shared/helpers/useInternationalFormatters";
import { ReportingMeasurement } from "../../shared/interfaces/ReportingMeasurement";
import { ReportingChartCheckbox } from "./ReportingChartCheckbox";

interface Props {
  loading?: boolean;
  data?: number[][];
  waveformConstants: WaveformConstantValues;
  reportingMeasurement?: ReportingMeasurement;
  isExpertOrGreater?: boolean;
  isEaEmployee?: boolean;
  onCheckboxChange?: (isChecked: boolean, chartIndex?: number) => void;
  onClearSelectedCharts?: () => void;
  isHistorical?: boolean;
  onChartIndexChange?: (isHistorical: boolean, chartIndex?: number) => void;
  tdr?: [number, number, number];
  setTdr?: (value: [number, number, number]) => void;
  tdrId?: number;
  setTdrId?: (value?: number) => void;
  cableLengthUpdated?: boolean;
  setCableLengthUpdated?: (value: boolean) => void;
  onClearGraphControl?: () => void;
  resetPlotLines?: boolean;
  setResetPlotLines?: (value: boolean) => void;
  reflow?: number;
  resultType?: string;
  flatBuffer?: boolean;
}

export function WaveformChart(props: Props) {
  const { t } = useTranslation();
  const { formatNumber } = useInternationalFormatters();

  const checkboxAvailable =
    config.reportsEnabled && props.isEaEmployee && props.isExpertOrGreater && props.onCheckboxChange;

  const [firstSeries, setFirstSeries] = useState(0);
  const [seriesCount, setSeriesCount] = useState(1);
  const [showingAllWaveforms, setShowingAllWaveforms] = useState(false);

  const { reportingMeasurement, onClearSelectedCharts, onClearGraphControl, resetPlotLines, setResetPlotLines } = props;

  const chartTooltipOptions: Highcharts.TooltipOptions = {
    formatter: function () {
      const formattedXValue = formatNumber(Number(this.x), 3);
      const formattedYValue = formatNumber(Number(this.y), 3);

      return `${formattedYValue} ${t(props.waveformConstants.YAxisUnitTranslationKey)}<br /><span style="color:${this.color}">\u25CF</span> ${this.series.name}: <b>${formattedXValue} ${t(props.waveformConstants.XAxisUnitTranslationKey)}</b><br/>`;
    },
  };

  const paginatorTemplate = {
    layout: "FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink RowsPerPageDropdown",

    RowsPerPageDropdown: (options: any) => {
      return (
        <React.Fragment>
          <Button variant="contained" color="primary" className="ml-1" onClick={toggleShowAllWaveforms}>
            {showingAllWaveforms ? t("common.showOne") : t("common.showAll")}{" "}
          </Button>
          {config.reportsEnabled &&
            props.isEaEmployee &&
            props.isExpertOrGreater &&
            props.resultType !== "byHVSubstation" && (
              <Button variant="contained" color="secondary" className="ml-1" onClick={clearSelectedCharts}>
                {t("common.clearSelectedCharts")}
              </Button>
            )}
          {!props.isHistorical && (
            <Button variant="contained" color="secondary" className="ml-1" onClick={clearGraphControl}>
              {t("common.clearGraphControl")}
            </Button>
          )}
        </React.Fragment>
      );
    },
  } as PaginatorTemplate;

  function toggleShowAllWaveforms(event: any) {
    setShowingAllWaveforms(!showingAllWaveforms);
  }

  function clearSelectedCharts() {
    if (onClearSelectedCharts) {
      onClearSelectedCharts();
    }
  }

  function clearGraphControl() {
    if (onClearGraphControl) {
      onClearGraphControl();
    }
  }

  useEffect(() => {
    if (showingAllWaveforms) {
      setSeriesCount(props.data?.length || 1);
      setFirstSeries(0);
    } else {
      setSeriesCount(1);
      setFirstSeries(0);
    }
  }, [showingAllWaveforms, props.data?.length]);

  const chartTitle = "";

  const chartYAxisSettings: Highcharts.YAxisOptions[] = [
    {
      title: {
        text: t(props.waveformConstants.YAxisUnitTranslationKey),
      },
      opposite: false,
    },
  ];

  const xAxisTitle = t(props.waveformConstants.XAxisUnitTranslationKey);

  const series = useMemo(() => {
    const seriesColors = [
      "#058DC7",
      "#50B432",
      "#ED561B",
      "#DDDF00",
      "#24CBE5",
      "#64E572",
      "#FF9655",
      "#FFF263",
      "#6AF9C4",
    ];

    const scale = props.waveformConstants.Scale;
    const offset = props.waveformConstants.Offset;
    const xStart = props.waveformConstants.XStart;
    const interval = props.waveformConstants.Interval;

    return props.data?.slice(firstSeries, firstSeries + seriesCount).map((waveformData, index) => {
      const waveformIndex = firstSeries + index;
      const colorIndex = waveformIndex % seriesColors.length;

      const scaledData = waveformData.map((dataValue) => {
        return scale * dataValue + offset;
      });

      return {
        data: props.flatBuffer != null && props.flatBuffer ? waveformData : scaledData,
        pointStart: xStart,
        pointInterval: interval,
        color: seriesColors[colorIndex],
        name: `${t("common.waveform")} ${(waveformIndex + 1).toString()}`,
      } as Partial<Highcharts.SeriesOptionsType>;
    });
  }, [props.waveformConstants, firstSeries, props.data, seriesCount, t, props.flatBuffer]);

  const onContentPageChange = (event: any) => {
    setFirstSeries(event.first);
    if (props.onChartIndexChange) {
      if (showingAllWaveforms) {
        props.onChartIndexChange(props.isHistorical || false, undefined);
      } else {
        props.onChartIndexChange(props.isHistorical || false, event.first);
      }
    }
  };

  const handleCheckboxChange = (isChecked: boolean, currentPageIndex: number) => {
    if (props.onCheckboxChange) {
      if (showingAllWaveforms) {
        props.onCheckboxChange(isChecked, undefined);
      } else {
        props.onCheckboxChange(isChecked, currentPageIndex);
      }
    }
  };

  const checkIfCheckboxIsChecked = (currentPageIndex: number) => {
    if (reportingMeasurement != null) {
      if (showingAllWaveforms) {
        return reportingMeasurement.chartIndex === null;
      } else {
        return reportingMeasurement.chartIndex === currentPageIndex;
      }
    }
    return false;
  };

  if (props.loading) {
    return (
      <div className="d-flex justify-content-center">
        <CircularProgress size={65} />
      </div>
    );
  } else
    return (
      <Stack height="100%">
        {props.data && props.data?.length > 1 && (
          <Paginator
            first={firstSeries}
            rows={seriesCount}
            totalRecords={props.data?.length}
            onPageChange={onContentPageChange}
            template={paginatorTemplate}
            style={{ backgroundColor: "inherit" }}
          />
        )}

        {series && (
          <>
            <LineChart
              series={series}
              chartTitle={chartTitle}
              yAxisSettings={chartYAxisSettings}
              xAxisTitle={xAxisTitle}
              tooltipOptions={chartTooltipOptions}
              tdr={props.tdr}
              setTdr={props.setTdr}
              tdrId={props.tdrId}
              setTdrId={props.setTdrId}
              cableLengthUpdated={props.cableLengthUpdated}
              setCableLengthUpdated={props.setCableLengthUpdated}
              resetPlotLines={resetPlotLines}
              setResetPlotLines={setResetPlotLines}
              reflow={props.reflow}
            />
            {checkboxAvailable && (
              <ReportingChartCheckbox
                isChecked={checkIfCheckboxIsChecked(firstSeries)}
                onChange={(isChecked) => handleCheckboxChange(isChecked, firstSeries)}
              />
            )}
          </>
        )}
      </Stack>
    );
}
